/**
 * 63.4's future, partial destruction step 2 (ARCHITECTURE §5.13): each kit tree's forms as its health falls, per drawn
 * variant (core/treeDraws.ts `treeVariant`), one entry per `PROPS.flora.forms` (constants/damage.ts) in its order — the
 * wood (as constants/treeWood.ts' `whole`) and the crown (as constants/treeFoliage.ts') of lane E's damaged model at the
 * form's level. Measured by rendering/treeShapeFit.ts `fitTreeFormShapes`, written by
 * `scripts/fitTreeFormShapes.ts`, held against the models by tests/treeFormShapes.test.ts. **Do not hand-edit:** re-run
 * the script after changing a tree model or the forms. Read by nothing in play until step 3 (the `partialDestruction`
 * switch); the classic tree types have no damaged model and no row.
 */
import type { WoodPiece } from './treeWood';
import type { FoliageBlob } from './treeFoliage';

export interface TreeFormShape { wood: readonly WoodPiece[]; blobs: readonly FoliageBlob[]; cones: readonly WoodPiece[] }
export interface TreeFormShapeRow { variants: readonly (readonly TreeFormShape[])[] }

export const TREE_FORM_SHAPES: Record<string, TreeFormShapeRow> = {
  puffElm: { variants: [
      [
        { wood: [[0, 0, 0, 0.02, 0, 0.13, 0, 0.012]],
          blobs: [[-0.002, 0.1835, 0.0018, 0.0767], [-0.0418, 0.153, -0.0231, 0.0524]],
          cones: [] },
        { wood: [[0, 0, 0, 0.02, 0, 0.13, 0, 0.012]],
          blobs: [[-0.0016, 0.1689, 0.0014, 0.0604]],
          cones: [] },
      ],
      [
        { wood: [[0, 0, 0, 0.02, 0, 0.13, 0, 0.012]],
          blobs: [[0.0001, 0.189, -0.0016, 0.0758], [0.0527, 0.1554, 0.019, 0.054]],
          cones: [] },
        { wood: [[0, 0, 0, 0.02, 0, 0.13, 0, 0.012]],
          blobs: [[0.0437, 0.15, 0.0163, 0.0426]],
          cones: [] },
      ],
      [
        { wood: [[0, 0, 0, 0.02, 0, 0.13, 0, 0.012]],
          blobs: [[-0.0037, 0.1793, 0.0013, 0.079], [0.05, 0.1554, 0.0146, 0.0566]],
          cones: [] },
        { wood: [[0, 0, 0, 0.02, 0, 0.13, 0, 0.012]],
          blobs: [[0.0416, 0.15, 0.0127, 0.0446]],
          cones: [] },
      ],
  ] },
  paleBirch: { variants: [
      [
        { wood: [[0, 0, 0, 0.012, 0, 0.32, 0, 0.007], [0, 0.18, 0, 0.004, 0.005, 0.2298, 0, 0.0024], [0, 0.22, 0, 0.004, -0.0044, 0.2701, 0.0025, 0.0024], [0, 0.27, 0, 0.004, 0.0008, 0.2861, -0.0012, 0.0024], [0, 0.31, 0, 0.004, -0.0002, 0.3184, 0.0002, 0.0024], [0, 0.13, 0, 0.004, 0.0014, 0.1482, 0.0014, 0.0024]],
          blobs: [[0.0353, 0.202, 0.0013, 0.0363], [-0.0281, 0.2395, 0.0171, 0.0327], [0.0266, 0.161, 0.0257, 0.0256]],
          cones: [] },
        { wood: [[0, 0, 0, 0.012, 0, 0.32, 0, 0.007], [0, 0.18, 0, 0.004, 0.0017, 0.1974, 0, 0.0024], [0, 0.22, 0, 0.004, -0.0044, 0.2701, 0.0025, 0.0024], [0, 0.27, 0, 0.004, 0.0008, 0.2861, -0.0012, 0.0024], [0, 0.31, 0, 0.004, -0.0002, 0.3184, 0.0002, 0.0024], [0, 0.13, 0, 0.004, 0.0014, 0.1482, 0.0014, 0.0024]],
          blobs: [[0.0283, 0.2044, 0.0026, 0.0286], [0.0214, 0.1721, 0.0218, 0.0202]],
          cones: [] },
      ],
      [
        { wood: [[0, 0, 0, 0.012, 0, 0.32, 0, 0.007], [0, 0.18, 0, 0.004, 0.0017, 0.1974, 0, 0.0024], [0, 0.22, 0, 0.004, -0.0015, 0.2375, 0.0009, 0.0024], [0, 0.27, 0, 0.004, 0.0023, 0.3159, -0.0034, 0.0024], [0, 0.31, 0, 0.004, -0.0006, 0.3341, 0.0006, 0.0024], [0, 0.13, 0, 0.004, 0.0039, 0.1821, 0.0039, 0.0024]],
          blobs: [[0.035, 0.1997, -0.0023, 0.0348], [0.0168, 0.2919, -0.0255, 0.0309], [0.0275, 0.1567, 0.0238, 0.0255]],
          cones: [] },
        { wood: [[0, 0, 0, 0.012, 0, 0.32, 0, 0.007], [0, 0.18, 0, 0.004, 0.0017, 0.1974, 0, 0.0024], [0, 0.22, 0, 0.004, -0.0015, 0.2375, 0.0009, 0.0024], [0, 0.27, 0, 0.004, 0.0023, 0.3159, -0.0034, 0.0024], [0, 0.31, 0, 0.004, -0.0002, 0.3184, 0.0002, 0.0024], [0, 0.13, 0, 0.004, 0.0039, 0.1821, 0.0039, 0.0024]],
          blobs: [[0.0296, 0.1978, -0.0021, 0.0274], [0.0237, 0.164, 0.0186, 0.0201]],
          cones: [] },
      ],
      [
        { wood: [[0, 0, 0, 0.012, 0, 0.32, 0, 0.007], [0, 0.18, 0, 0.004, 0.0017, 0.1974, 0, 0.0024], [0, 0.22, 0, 0.004, -0.0044, 0.2701, 0.0025, 0.0024], [0, 0.27, 0, 0.004, 0.0008, 0.2861, -0.0012, 0.0024], [0, 0.31, 0, 0.004, -0.0006, 0.3341, 0.0006, 0.0024], [0, 0.13, 0, 0.004, 0.0014, 0.1482, 0.0014, 0.0024]],
          blobs: [[0.0335, 0.2007, 0.0021, 0.0353], [-0.0293, 0.2404, 0.0162, 0.0332], [0.0264, 0.1587, 0.0256, 0.0258]],
          cones: [] },
        { wood: [[0, 0, 0, 0.012, 0, 0.32, 0, 0.007], [0, 0.18, 0, 0.004, 0.0017, 0.1974, 0, 0.0024], [0, 0.22, 0, 0.004, -0.0044, 0.2701, 0.0025, 0.0024], [0, 0.27, 0, 0.004, 0.0008, 0.2861, -0.0012, 0.0024], [0, 0.31, 0, 0.004, -0.0002, 0.3184, 0.0002, 0.0024], [0, 0.13, 0, 0.004, 0.0014, 0.1482, 0.0014, 0.0024]],
          blobs: [[0.028, 0.2027, 0.0019, 0.0278], [0.0224, 0.1696, 0.0204, 0.0203]],
          cones: [] },
      ],
  ] },
  stackFir: { variants: [
      [
        { wood: [[0, 0, 0, 0.016, 0, 0.08, 0, 0.01]],
          blobs: [],
          cones: [[0.0005, 0.05, -0.0013, 0.0825, -0.0003, 0.1407, 0.0009, 0], [-0.0004, 0.1036, -0.0012, 0.066, 0.0005, 0.1835, 0.0007, 0]] },
        { wood: [[0, 0, 0, 0.016, 0, 0.08, 0, 0.01]],
          blobs: [],
          cones: [[0.0005, 0.05, -0.0013, 0.065, -0.0001, 0.1215, 0.0004, 0]] },
      ],
      [
        { wood: [[0, 0, 0, 0.016, 0, 0.08, 0, 0.01]],
          blobs: [],
          cones: [[0, 0.05, 0, 0.0825, 0, 0.1407, 0, 0], [-0.0006, 0.1036, 0.0005, 0.066, 0.0006, 0.1835, -0.0005, 0]] },
        { wood: [[0, 0, 0, 0.016, 0, 0.08, 0, 0.01]],
          blobs: [],
          cones: [[0, 0.05, 0, 0.065, 0, 0.1215, 0, 0]] },
      ],
      [
        { wood: [[0, 0, 0, 0.016, 0, 0.08, 0, 0.01]],
          blobs: [],
          cones: [[0.0001, 0.05, 0.0007, 0.0825, -0.0001, 0.1407, -0.0004, 0], [0.0005, 0.1036, -0.0009, 0.066, -0.0005, 0.1835, 0.0011, 0]] },
        { wood: [[0, 0, 0, 0.016, 0, 0.08, 0, 0.01]],
          blobs: [],
          cones: [[0.0001, 0.05, 0.0007, 0.065, 0, 0.1215, -0.0002, 0]] },
      ],
  ] },
  lanternFir: { variants: [
      [
        { wood: [[0, 0, 0, 0.016, 0, 0.08, 0, 0.01]],
          blobs: [],
          cones: [[0.0004, 0.05, -0.0001, 0.0825, -0.0003, 0.1407, 0.0001, 0], [0.0004, 0.1036, 0.0002, 0.066, -0.0003, 0.1835, -0.0002, 0], [0.0005, 0.1573, 0.0003, 0.0495, -0.0004, 0.2262, -0.0004, 0]] },
        { wood: [[0, 0, 0, 0.016, 0, 0.08, 0, 0.01]],
          blobs: [],
          cones: [[0.0004, 0.05, -0.0001, 0.065, -0.0001, 0.1215, 0, 0], [0.0004, 0.0923, 0.0001, 0.052, -0.0001, 0.1552, -0.0002, 0], [0.0005, 0.1345, 0.0002, 0.039, -0.0002, 0.1888, -0.0003, 0]] },
      ],
      [
        { wood: [[0, 0, 0, 0.016, 0, 0.08, 0, 0.01]],
          blobs: [],
          cones: [[-0.0008, 0.05, -0.0012, 0.0825, 0.0005, 0.1407, 0.0008, 0], [0.0001, 0.1036, -0.0011, 0.066, -0.0004, 0.1835, 0.0007, 0], [-0.0004, 0.1573, -0.0007, 0.0495, 0.0001, 0.2262, 0.0003, 0], [-0.0008, 0.2109, -0.0004, 0.033, 0.0005, 0.2689, 0, 0]] },
        { wood: [[0, 0, 0, 0.016, 0, 0.08, 0, 0.01]],
          blobs: [],
          cones: [[-0.0008, 0.05, -0.0012, 0.065, 0.0003, 0.1215, 0.0004, 0], [-0.0001, 0.0923, -0.0011, 0.052, -0.0005, 0.1552, 0.0003, 0]] },
      ],
      [
        { wood: [[0, 0, 0, 0.016, 0, 0.08, 0, 0.01]],
          blobs: [],
          cones: [[-0.0005, 0.05, 0.0006, 0.0825, 0.0003, 0.1407, -0.0004, 0], [0, 0.1036, -0.0009, 0.066, -0.0001, 0.1835, 0.0011, 0], [-0.0002, 0.1573, 0, 0.0495, 0.0001, 0.2262, 0.0002, 0], [-0.0007, 0.2109, -0.0003, 0.033, 0.0005, 0.2689, 0.0005, 0]] },
        { wood: [[0, 0, 0, 0.016, 0, 0.08, 0, 0.01]],
          blobs: [],
          cones: [[-0.0005, 0.05, 0.0006, 0.065, 0.0001, 0.1215, -0.0002, 0], [-0.0001, 0.0923, -0.0006, 0.052, -0.0002, 0.1552, 0.001, 0]] },
      ],
  ] },
  windPine: { variants: [
      [
        { wood: [[0, 0, 0, 0.015, 0.0642, 0.1894, 0, 0.008], [0.0385, 0.1137, 0, 0.004, -0.0136, 0.1571, 0.0174, 0.0024]],
          blobs: [[0.0843, 0.1966, -0.0021, 0.0662]],
          cones: [] },
        { wood: [[0, 0, 0, 0.015, 0.0642, 0.1894, 0, 0.008], [0.0385, 0.1137, 0, 0.004, 0.0203, 0.1288, 0.0061, 0.0024]],
          blobs: [[0.0801, 0.195, 0, 0.0522]],
          cones: [] },
      ],
      [
        { wood: [[0, 0, 0, 0.015, 0.0589, 0.1911, 0, 0.008], [0.0353, 0.1147, 0, 0.004, 0.0171, 0.1299, 0.0061, 0.0024]],
          blobs: [[0.0787, 0.1984, -0.0021, 0.0661]],
          cones: [] },
        { wood: [[0, 0, 0, 0.015, 0.0589, 0.1911, 0, 0.008], [0.0353, 0.1147, 0, 0.004, 0.0171, 0.1299, 0.0061, 0.0024]],
          blobs: [[0.0745, 0.1968, 0, 0.0521]],
          cones: [] },
      ],
      [
        { wood: [[0, 0, 0, 0.015, 0.0574, 0.1916, 0, 0.008], [0.0344, 0.1149, 0, 0.004, -0.0176, 0.1584, 0.0174, 0.0024]],
          blobs: [[0.0731, 0.2001, 0.0018, 0.0633]],
          cones: [] },
        { wood: [[0, 0, 0, 0.015, 0.0574, 0.1916, 0, 0.008], [0.0344, 0.1149, 0, 0.004, 0.0162, 0.1301, 0.0061, 0.0024]],
          blobs: [[0.0698, 0.1983, 0.0031, 0.0499]],
          cones: [] },
      ],
  ] },
  bottleTree: { variants: [
      [
        { wood: [[0, 0, 0, 0.02, 0, 0.03, 0, 0.045], [0, 0.03, 0, 0.045, 0, 0.08, 0, 0.05], [0, 0.08, 0, 0.05, 0, 0.13, 0, 0.04], [0, 0.13, 0, 0.04, 0, 0.17, 0, 0.018], [0, 0.17, 0, 0.018, 0, 0.19, 0, 0.014], [0, 0.19, 0, 0.005, 0.0461, 0.2244, 0.017, 0.003], [0, 0.19, 0, 0.005, -0.0036, 0.2244, 0.049, 0.003], [0, 0.19, 0, 0.005, -0.0478, 0.2244, -0.0116, 0.003], [0, 0.19, 0, 0.005, 0.0005, 0.2244, -0.0492, 0.003]],
          blobs: [[0.0417, 0.2372, 0.0148, 0.0224], [-0.0441, 0.238, -0.0096, 0.022]],
          cones: [] },
        { wood: [[0, 0, 0, 0.02, 0, 0.03, 0, 0.045], [0, 0.03, 0, 0.045, 0, 0.08, 0, 0.05], [0, 0.08, 0, 0.05, 0, 0.13, 0, 0.04], [0, 0.13, 0, 0.04, 0, 0.17, 0, 0.018], [0, 0.17, 0, 0.018, 0, 0.19, 0, 0.014], [0, 0.19, 0, 0.005, 0.0161, 0.202, 0.006, 0.003], [0, 0.19, 0, 0.005, -0.0036, 0.2244, 0.049, 0.003], [0, 0.19, 0, 0.005, -0.0167, 0.202, -0.0041, 0.003], [0, 0.19, 0, 0.005, 0.0002, 0.202, -0.0172, 0.003]],
          blobs: [],
          cones: [] },
      ],
      [
        { wood: [[0, 0, 0, 0.02, 0, 0.03, 0, 0.045], [0, 0.03, 0, 0.045, 0, 0.08, 0, 0.05], [0, 0.08, 0, 0.05, 0, 0.13, 0, 0.04], [0, 0.13, 0, 0.04, 0, 0.17, 0, 0.018], [0, 0.17, 0, 0.018, 0, 0.19, 0, 0.014], [0, 0.19, 0, 0.005, 0.0165, 0.202, 0.0049, 0.003], [0, 0.19, 0, 0.005, -0.0018, 0.202, 0.0171, 0.003], [0, 0.19, 0, 0.005, -0.0418, 0.2244, -0.0258, 0.003], [0, 0.19, 0, 0.005, 0.0033, 0.202, -0.0169, 0.003]],
          blobs: [[-0.0394, 0.2376, -0.0244, 0.0231]],
          cones: [] },
        { wood: [[0, 0, 0, 0.02, 0, 0.03, 0, 0.045], [0, 0.03, 0, 0.045, 0, 0.08, 0, 0.05], [0, 0.08, 0, 0.05, 0, 0.13, 0, 0.04], [0, 0.13, 0, 0.04, 0, 0.17, 0, 0.018], [0, 0.17, 0, 0.018, 0, 0.19, 0, 0.014], [0, 0.19, 0, 0.005, 0.0165, 0.202, 0.0049, 0.003], [0, 0.19, 0, 0.005, -0.0018, 0.202, 0.0171, 0.003], [0, 0.19, 0, 0.005, -0.0146, 0.202, -0.009, 0.003], [0, 0.19, 0, 0.005, 0.0033, 0.202, -0.0169, 0.003]],
          blobs: [],
          cones: [] },
      ],
      [
        { wood: [[0, 0, 0, 0.02, 0, 0.03, 0, 0.045], [0, 0.03, 0, 0.045, 0, 0.08, 0, 0.05], [0, 0.08, 0, 0.05, 0, 0.13, 0, 0.04], [0, 0.13, 0, 0.04, 0, 0.17, 0, 0.018], [0, 0.17, 0, 0.018, 0, 0.19, 0, 0.014], [0, 0.19, 0, 0.005, 0.0476, 0.2244, 0.0124, 0.003], [0, 0.19, 0, 0.005, -0.0057, 0.202, 0.0162, 0.003], [0, 0.19, 0, 0.005, -0.0172, 0.202, -0.0012, 0.003], [0, 0.19, 0, 0.005, 0.0029, 0.202, -0.0169, 0.003]],
          blobs: [[0.043, 0.2372, 0.012, 0.0233]],
          cones: [] },
        { wood: [[0, 0, 0, 0.02, 0, 0.03, 0, 0.045], [0, 0.03, 0, 0.045, 0, 0.08, 0, 0.05], [0, 0.08, 0, 0.05, 0, 0.13, 0, 0.04], [0, 0.13, 0, 0.04, 0, 0.17, 0, 0.018], [0, 0.17, 0, 0.018, 0, 0.19, 0, 0.014], [0, 0.19, 0, 0.005, 0.0476, 0.2244, 0.0124, 0.003], [0, 0.19, 0, 0.005, -0.0057, 0.202, 0.0162, 0.003], [0, 0.19, 0, 0.005, -0.0172, 0.202, -0.0012, 0.003], [0, 0.19, 0, 0.005, 0.0029, 0.202, -0.0169, 0.003]],
          blobs: [],
          cones: [] },
      ],
  ] },
  snag: { variants: [
      [
        { wood: [[0.0013, 0, 0, 0.017, -0.0013, 0.26, 0, 0.008], [0, 0.1184, 0, 0.005, -0.0009, 0.1348, 0.0215, 0.003], [0, 0.1617, 0, 0.005, 0.0131, 0.1726, 0.0063, 0.003], [0, 0.1996, 0, 0.005, 0.067, 0.2484, 0.0001, 0.003], [0.0058, 0.2612, 0, 0.008, -0.0058, 0.2888, 0, 0.004]],
          blobs: [],
          cones: [] },
        { wood: [[0.0013, 0, 0, 0.017, -0.0013, 0.26, 0, 0.008], [0, 0.1184, 0, 0.005, -0.0009, 0.1348, 0.0215, 0.003], [0, 0.1617, 0, 0.005, 0.0131, 0.1726, 0.0063, 0.003], [0, 0.1996, 0, 0.005, 0.0235, 0.2167, 0, 0.003], [0.0058, 0.2612, 0, 0.008, 0.0018, 0.2709, 0, 0.004]],
          blobs: [],
          cones: [] },
      ],
      [
        { wood: [[0.0012, 0, 0, 0.017, -0.0012, 0.26, 0, 0.008], [0, 0.1538, 0, 0.005, 0.0148, 0.172, 0.0107, 0.003], [0, 0.128, 0, 0.005, 0.0048, 0.1458, -0.0188, 0.003], [0.0058, 0.2612, 0, 0.008, -0.0058, 0.2888, 0, 0.004]],
          blobs: [],
          cones: [] },
        { wood: [[0.0012, 0, 0, 0.017, -0.0012, 0.26, 0, 0.008], [0, 0.1538, 0, 0.005, 0.0148, 0.172, 0.0107, 0.003], [0, 0.128, 0, 0.005, 0.0048, 0.1458, -0.0188, 0.003], [0.0058, 0.2612, 0, 0.008, -0.0058, 0.2888, 0, 0.004]],
          blobs: [],
          cones: [] },
      ],
      [
        { wood: [[-0.0004, 0, 0, 0.017, 0.0004, 0.26, 0, 0.008], [0, 0.1211, 0, 0.005, 0.0482, 0.1799, 0.0423, 0.003], [0, 0.1342, 0, 0.005, 0.0114, 0.1514, 0.0205, 0.003], [0, 0.119, 0, 0.005, 0.0082, 0.1365, 0.0224, 0.003], [0.0058, 0.2612, 0, 0.008, -0.0058, 0.2888, 0, 0.004]],
          blobs: [],
          cones: [] },
        { wood: [[-0.0004, 0, 0, 0.017, 0.0004, 0.26, 0, 0.008], [0, 0.1211, 0, 0.005, 0.0482, 0.1799, 0.0423, 0.003], [0, 0.1342, 0, 0.005, 0.0114, 0.1514, 0.0205, 0.003], [0, 0.119, 0, 0.005, 0.0082, 0.1365, 0.0224, 0.003], [0.0058, 0.2612, 0, 0.008, -0.0058, 0.2888, 0, 0.004]],
          blobs: [],
          cones: [] },
      ],
  ] },
};
